// Reads a Brawl Online replay (.brep) as its bytes arrive, into the same
// events SlpStreamParser makes, so liveGameState and the friendlies board
// take either. push() resolves to the events completed by each new piece.
//
// The file is a 32-byte header (magic \x89BRP\r\n\x1a\n, version, header
// length), then chunks: a 4-letter tag and four little-endian uint32s
// (version, stored length, length, checksum), then the stored bytes, which
// are zlib-deflated when shorter than the length. INFO (JSON) opens the game:
// players, stage, rules, start time. Frame chunks follow while it runs; they
// are raw snapshots of the game's memory, so nothing is read from them. ENDG
// (JSON) closes it with each player's stocks and damage and the winner. So a
// game gives gameStart, and at its end gameTotals and gameEnd; there are no
// stocks or percents in between.
//
// Brawl Online's Dolphin keeps the file locked while it writes it, so in
// practice the whole replay is read once the game is over.

const MAGIC = [0x89, 0x42, 0x52, 0x50, 0x0d, 0x0a, 0x1a, 0x0a];
const FILE_HEADER_MINIMUM = 16;
const CHUNK_HEADER_LENGTH = 20;
const CHUNK_TAG = /^[A-Z]{4}$/;
const NOT_A_REPLAY = 'This file is not a Brawl Online replay.';

// Slippi's game-end method codes, which liveGameState reads. A runback,
// disconnect or stop has no code, so its result is unknown and isn't recorded.
const GameEndMethodCode = {
	time: 1,
	game: 2,
	quit: 7,
};

export default class BrepStreamParser {
	constructor() {
		this.buffer = new Uint8Array(0);
		this.headerRead = false;
		this.isComplete = false;
		this.players = [];
	}

	async push(bytes) {
		if(this.isComplete) {
			return [];
		}
		this.buffer = concat(this.buffer, bytes);
		const events = [];
		if(!this.headerRead && !this.readHeader()) {
			return events;
		}
		while(!this.isComplete && this.buffer.length >= CHUNK_HEADER_LENGTH) {
			const view = new DataView(this.buffer.buffer, this.buffer.byteOffset, this.buffer.byteLength);
			const tag = String.fromCharCode(...this.buffer.subarray(0, 4));
			if(!CHUNK_TAG.test(tag)) {
				throw new Error(NOT_A_REPLAY);
			}
			const storedLength = view.getUint32(8, true);
			const length = view.getUint32(12, true);
			if(this.buffer.length < CHUNK_HEADER_LENGTH + storedLength) {
				break;
			}
			const stored = this.buffer.slice(CHUNK_HEADER_LENGTH, CHUNK_HEADER_LENGTH + storedLength);
			this.buffer = this.buffer.subarray(CHUNK_HEADER_LENGTH + storedLength);
			if(tag === 'INFO') {
				events.push(this.gameStart(await readJson(stored, length)));
			}
			else if(tag === 'ENDG') {
				events.push(...this.gameEnd(await readJson(stored, length)));
				this.isComplete = true;
			}
		}
		return events;
	}

	readHeader() {
		if(this.buffer.length < FILE_HEADER_MINIMUM) {
			return false;
		}
		if(MAGIC.some((byte, index) => this.buffer[index] !== byte)) {
			throw new Error(NOT_A_REPLAY);
		}
		const headerLength = new DataView(this.buffer.buffer, this.buffer.byteOffset, this.buffer.byteLength).getUint32(12, true);
		if(this.buffer.length < headerLength) {
			return false;
		}
		this.buffer = this.buffer.subarray(headerLength);
		this.headerRead = true;
		return true;
	}

	gameStart(info) {
		const startStocks = info.rules?.stocks ?? null;
		this.players = (info.players || []).map(player => ({
			playerIndex: player.port - 1,
			port: player.port,
			characterId: player.characterId,
			characterColor: player.costume || 0,
			teamId: player.teamId ?? null,
			nametag: '',
			displayName: player.displayName || '',
			connectCode: player.connectCode || '',
			startStocks,
		}));
		return {
			type: 'gameStart',
			isTeams: !!info.isTeams,
			stageId: info.stageId ?? null,
			players: this.players,
			matchId: info.matchId || '',
			gameNumber: info.gameIndex ?? null,
			startedAt: info.startTime || null,
		};
	}

	gameEnd(end) {
		const endPlayers = end.players || [];
		const placements = [0, 1, 2, 3].map(playerIndex => {
			const player = this.players.find(candidate => candidate.playerIndex === playerIndex);
			if(!player || end.winnerPort === null || end.winnerPort === undefined) {
				return -1;
			}
			return player.port === end.winnerPort ? 0 : 1;
		});
		return [
			{
				type: 'gameTotals',
				frame: end.finalFrame ?? null,
				players: endPlayers.map(player => ({ playerIndex: player.port - 1, stocks: player.stocks, percent: player.damage })),
			},
			{
				type: 'gameEnd',
				gameEndMethod: GameEndMethodCode[end.endMethod] || 0,
				lrasInitiatorIndex: null,
				placements,
			},
		];
	}
}

async function readJson(stored, length) {
	const bytes = stored.length < length ? await inflate(stored) : stored;
	return JSON.parse(new TextDecoder('utf-8').decode(bytes));
}

export async function inflate(bytes) {
	const stream = new DecompressionStream('deflate');
	const writer = stream.writable.getWriter();
	writer.write(bytes).catch(() => {});
	writer.close().catch(() => {});
	const reader = stream.readable.getReader();
	const pieces = [];
	for(;;) {
		const { done, value } = await reader.read();
		if(done) {
			break;
		}
		pieces.push(value);
	}
	return pieces.reduce(concat, new Uint8Array(0));
}

function concat(first, second) {
	if(first.length === 0) {
		return second;
	}
	const joined = new Uint8Array(first.length + second.length);
	joined.set(first);
	joined.set(second, first.length);
	return joined;
}
